// Klientská logika pro skládání víceúrovňového stromu skupin stanic z groupLevels (viz doc/plans/station-group-layout.md sekce I.1)
// groupLevels[i].buckets[j] = { label, memberCodes: [station_group.code, ...] } — statické úrovně
// groupLevels[i].sourceId = station_group_source.id — spolu s memberCodes se přes codeToId (níže) resolvuje
// na aktuální station_group.id pro danou datasource větev (id se liší postgres/oracle, code je stabilní).
// groupLevels[i].dynamicDefType/dynamicSourceId/param — dynamické úrovně (elevation_interval/elevation_cluster/spatial_cluster);
// nemají předpočítané buckets, clustery se počítají zvlášť pro každého rodiče (viz computeDynamicGroups), protože
// např. "10 stanic na cluster" musí dát jiné clustery pro každý kraj zvlášť.
// memberGhIds: Map<station_group.id, Set<gh_id>> — členství podskupin, dodané voláním kódem přes useStationGroupMembers
// codeToId: Map<`${sourceId}:${code}`, station_group.id> — resolving code → aktuální id pro danou datasource větev
// stationsByGhId: Map<gh_id, {gh_id, elevation, latitude, longitude}> — potřebné jen pro dynamické úrovně

import { computeDynamicGroups, splitByCount, DIRECTION_LABELS } from './dynamicClustering'

// Sjednocení gh_id přes memberCodes jednoho kbelíku (přes codeToId resolvnuté na aktuální station_group.id)
function bucketGhIds(bucket, sourceId, memberGhIds, codeToId) {
  const result = new Set()
  for (const code of bucket.memberCodes) {
    const id = codeToId.get(`${sourceId}:${code}`)
    const ghIds = id != null ? memberGhIds.get(id) : null
    if (!ghIds) continue
    for (const gh of ghIds) result.add(gh)
  }
  return result
}

// Postaví strom uzlů: každý uzel má { label, ghIds: Set, children: [] }
// Uzel na úrovni i = kombinace (kbelík úrovně 0) ∩ (kbelík úrovně 1) ∩ ... ∩ (kbelík úrovně i)
//
// Úrovně se skrytým showAsTreeLevel=false se PROCHÁZEJÍ všechny (jejich buckety pořád zužují
// množinu stanic pro navazující úrovně jako průnik), jen se pro ně nevytvoří samostatný uzel ve
// stromu — děti skryté úrovně se místo toho zavěsí přímo pod posledního viditelného rodiče (nebo
// pod kořen, pokud jsou skryté úrovně na začátku). Bez tohoto rozlišení by se skrytá úroveň úplně
// vynechala z groupLevels a její filtr (např. "jen Profi") by přestal platit.
export function buildGroupTree(groupLevels, memberGhIds, stationsByGhId = new Map(), codeToId = new Map()) {
  // Rozdělí množinu gh_id podle splitSpec (viz doc/plans/group-context-merge-split.md kap. 4).
  // Vrací [{ suffix, ghIds: Set }].
  function splitGhIds(inputGhIds, spec) {
    const stations = [...inputGhIds].map(gh => stationsByGhId.get(gh)).filter(Boolean)
    if (spec.method === 'count') {
      const labels = DIRECTION_LABELS[spec.lang] ?? DIRECTION_LABELS.cs
      return splitByCount(stations, spec.parts ?? 'auto', Number(spec.perGroup) || 10)
        .map(part => ({ suffix: part.dir ? labels[part.dir] : null, ghIds: new Set(part.ghIds) }))
    }
    const n = Number(spec.param)
    if (!n || n <= 0) return [{ suffix: null, ghIds: new Set(inputGhIds) }]
    return computeDynamicGroups(stations, spec.method, n).map(g => ({ suffix: g.label, ghIds: new Set(g.ghIds) }))
  }

  // Aplikuje override (merges + splits) na výchozí buckety úrovně v jedné konkrétní větvi.
  function applyOverride(base, ov, parentGhIds) {
    let out = base.map(b => ({ ...b }))

    for (const s of ov.splits ?? []) {
      const idx = out.findIndex(b => b.memberCodes && b.memberCodes.length === 1 && b.memberCodes[0] === s.memberCode)
      if (idx === -1) continue
      const input = parentGhIds ? intersect(out[idx].ghIds, parentGhIds) : out[idx].ghIds
      const pieces = splitGhIds(input, s)
      const baseLabel = out[idx].label
      out.splice(idx, 1, ...pieces.map(p => ({
        label: p.suffix ? `${baseLabel} ${p.suffix}` : baseLabel,
        ghIds: p.ghIds,
      })))
    }

    for (const m of ov.merges ?? []) {
      const codeSet = new Set(m.memberCodes)
      out = out.filter(b => !(b.memberCodes && b.memberCodes.length > 0 && b.memberCodes.every(c => codeSet.has(c))))
      const merged = new Set()
      for (const code of m.memberCodes) {
        const id = codeToId.get(`${ov._sourceId}:${code}`)
        const ghIds = id != null ? memberGhIds.get(id) : null
        if (ghIds) for (const gh of ghIds) merged.add(gh)
      }
      out.push({ label: m.label, ghIds: merged })
    }
    return out
  }

  function levelGhIdsByBucket(level, parentGhIds, parentPath) {
    if (level.dynamicDefType) {
      const inputGhIds = parentGhIds ?? new Set(stationsByGhId.keys())
      const stations = [...inputGhIds].map(gh => stationsByGhId.get(gh)).filter(Boolean)
      // spatial_count: level.param nese režim ('auto'|'2'|'4'|'5'|'9'), level.dynamicLang jazyk zkratek
      if (level.dynamicDefType === 'spatial_count') {
        const labels = DIRECTION_LABELS[level.dynamicLang] ?? DIRECTION_LABELS.cs
        const perGroup = Number(level.perGroup) || 10
        return splitByCount(stations, level.param ?? 'auto', perGroup)
          .map(part => ({ label: part.dir ? labels[part.dir] : 'Vše', ghIds: new Set(part.ghIds) }))
      }
      const n = Number(level.param)
      if (!n || n <= 0) return []
      return computeDynamicGroups(stations, level.dynamicDefType, n).map(g => ({ label: g.label, ghIds: new Set(g.ghIds) }))
    }
    const base = level.buckets.map(bucket => ({
      label: bucket.label,
      memberCodes: bucket.memberCodes,
      ghIds: bucketGhIds(bucket, level.sourceId, memberGhIds, codeToId),
    }))
    const ov = (level.overrides ?? []).find(o => pathEquals(o.parentPath, parentPath))
    if (!ov) return base
    return applyOverride(base, { ...ov, _sourceId: level.sourceId }, parentGhIds)
  }

  // Zpracuje groupLevels[levelIdx..] nad danou vstupní množinou (parentGhIds = null značí "všechny
  // stanice", tj. kořen). Vrací { ghIds, children }:
  // - ghIds = efektivní množina PO aplikaci téhle úrovně (a všech navazujících skrytých úrovní) —
  //   používá ji volající k vlastnímu průniku, ať šlo o viditelnou nebo skrytou úroveň.
  // - children = uzly, které se mají zavěsit pod poslední viditelný uzel (může jít o děti až
  //   z hlubší úrovně, pokud mezi nimi leží jedna nebo víc skrytých úrovní).
  function buildFrom(levelIdx, parentGhIds, parentPath) {
    const level = groupLevels[levelIdx]
    if (!level) return { ghIds: parentGhIds, children: [] }

    const buckets = levelGhIdsByBucket(level, parentGhIds, parentPath)
    const isVisible = level.showAsTreeLevel !== false

    if (isVisible) {
      const children = buckets
        .map(({ label, ghIds: own }) => {
          const ghIds = parentGhIds ? intersect(parentGhIds, own) : own
          if (ghIds.size === 0) return null
          const deeper = buildFrom(levelIdx + 1, ghIds, [...parentPath, label])
          return { label, ghIds: deeper.ghIds, children: deeper.children }
        })
        .filter(Boolean)
      // ghIds téhle úrovně navenek = sjednocení (pro účely průniku rodičovské skryté úrovně nad ní,
      // pokud by nějaká byla) — v praxi se ale nepoužije, protože viditelná úroveň vrací children
      // rovnou volajícímu buildGroupTree/rodičovskému viditelnému uzlu.
      return { ghIds: parentGhIds, children }
    }

    // Skrytá úroveň: sjednoť průnik přes všechny zaškrtnuté buckety, pokračuj další úrovní nad
    // zúženou množinou — vlastní uzel nevytváří, jen předá zúžení a children dál. Do parentPath
    // se ale započítá (override cílí na logickou větev, ne na vizuální — viz plán kap. 3.1).
    const ownUnion = new Set()
    for (const { ghIds } of buckets) for (const gh of ghIds) ownUnion.add(gh)
    const narrowed = parentGhIds ? intersect(parentGhIds, ownUnion) : ownUnion
    if (narrowed.size === 0) return { ghIds: narrowed, children: [] }
    const hiddenLabel = buckets.length === 1 ? buckets[0].label : (level.buckets[0]?.label ?? '')
    return buildFrom(levelIdx + 1, narrowed, [...parentPath, hiddenLabel])
  }

  return buildFrom(0, null, []).children
}

// Cesta labelů nadřízenými buckety je shodná (stejná délka a prvky)
function pathEquals(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

function intersect(a, b) {
  const [small, big] = a.size <= b.size ? [a, b] : [b, a]
  const result = new Set()
  for (const v of small) if (big.has(v)) result.add(v)
  return result
}

// Sjednocení gh_id uzlu a všech jeho potomků rekurzivně (klik na uzel s dětmi, bod I.1.8)
export function unionNodeGhIds(node) {
  if (!node.children || node.children.length === 0) return node.ghIds
  const result = new Set()
  for (const child of node.children) {
    for (const gh of unionNodeGhIds(child)) result.add(gh)
  }
  return result
}

// Najde uzel podle selectedPath = [{levelIdx, bucketLabel}, ...] (levelIdx odpovídá indexu ve visibleLevels, ne v groupLevels)
export function findNodeByPath(tree, selectedPath) {
  let nodes = tree
  let node = null
  for (const step of selectedPath) {
    node = nodes.find(n => n.label === step.bucketLabel)
    if (!node) return null
    nodes = node.children
  }
  return node
}

// Efektivní množina gh_id pro danou selectedPath nad daným tree (null selectedPath/prázdný tree = všechny stanice, vrací null)
export function computeEffectiveGroupGhIds(groupLevels, selectedPath, memberGhIds, codeToId) {
  if (!groupLevels || groupLevels.length === 0) return null
  const tree = buildGroupTree(groupLevels, memberGhIds, undefined, codeToId)
  if (tree.length === 0) return null
  if (!selectedPath || selectedPath.length === 0) return null
  const node = findNodeByPath(tree, selectedPath)
  if (!node) return null
  return unionNodeGhIds(node)
}
